import React from "react";
import { Card, CardTitle, Avatar } from "./ui";

const PRESENCE = {
  online: ["#E9F8EF", "#1E7A46", "#22C05C", "Online"],
  ausente: ["#FFF3DC", "#B7791F", "#F5A623", "Ausente"],
  offline: ["#EEF1F4", "#5B6472", "#9AA3AF", "Offline"],
};

// Escala do heatmap: ≤6 verde, 7–11 âmbar, ≥12 vermelho; null = neutro.
const heatColors = (min) => {
  if (min == null) return ["#F4F4F6", "#B8B8C0"];
  if (min <= 6) return ["#E9F8EF", "#1E7A46"];
  if (min <= 11) return ["#FFF3DC", "#B7791F"];
  return ["#FDE8E8", "#C0392B"];
};

// Linha 4 — atendentes online + espera por hora (heatmap) + sem resposta.
const Operations = ({
  V,
  atendentes,
  atendentesOnline,
  heatmap,
  semRespostaPorConexao,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: ".9fr 1.5fr 1fr",
      gap: 12,
    }}
  >
    <Card V={V} style={{ gap: 9 }}>
      <CardTitle V={V}>Atendentes online</CardTitle>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: V.sublabel }}>
        {`${atendentesOnline} de ${atendentes.length} disponíveis agora`}
      </div>
      {atendentes.map((a) => {
        const [bg, col, dot, label] = PRESENCE[a.presence] || PRESENCE.offline;
        return (
          <div
            key={a.userId}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Avatar name={a.name} V={V} />
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 800,
                color: V.textStrong,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {a.name}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: bg,
                color: col,
                fontSize: 10,
                fontWeight: 800,
                padding: "1px 8px",
                borderRadius: 999,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: dot,
                }}
              />
              {label}
            </span>
          </div>
        );
      })}
      {!atendentes.length && (
        <span style={{ fontSize: 11, color: V.labelMuted }}>
          Nenhum atendente
        </span>
      )}
    </Card>

    <Card V={V} style={{ gap: 9 }}>
      <CardTitle
        V={V}
        right={
          <span style={{ fontSize: 10, fontWeight: 700, color: V.labelMuted }}>
            minutos até a 1ª resposta · hoje
          </span>
        }
      >
        Espera média por hora
      </CardTitle>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12,1fr)",
          gap: 4,
        }}
      >
        {heatmap.map((h) => {
          const [bg, col] = heatColors(h.min);
          return (
            <div
              key={h.hour}
              title={`${h.hour} · ${h.min == null ? "—" : `${h.min}min`}`}
              style={{
                height: 34,
                borderRadius: 6,
                background: bg,
                color: col,
                fontSize: 10.5,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {h.min == null ? "·" : h.min}
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12,1fr)",
          gap: 4,
        }}
      >
        {heatmap.map((h) => (
          <span
            key={h.hour}
            style={{
              fontSize: 9,
              fontWeight: 800,
              color: V.labelMuted,
              textAlign: "center",
            }}
          >
            {h.hour}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 2 }}>
        {[
          ["#E9F8EF", "até 6min"],
          ["#FFF3DC", "7–11min"],
          ["#FDE8E8", "12min ou mais"],
        ].map(([c, l]) => (
          <span
            key={l}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 10,
              fontWeight: 800,
              color: V.sublabel,
            }}
          >
            <span
              style={{ width: 8, height: 8, borderRadius: 2, background: c }}
            />
            {l}
          </span>
        ))}
      </div>
    </Card>

    <Card V={V} style={{ gap: 10 }}>
      <CardTitle V={V}>Sem resposta por conexão</CardTitle>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: V.sublabel }}>
        mensagens recebidas sem retorno em 1h
      </div>
      {semRespostaPorConexao.length ? (
        semRespostaPorConexao.map((c) => {
          const level = c.pct >= 20 ? V.bad : c.pct >= 10 ? V.warn : V.good;
          const bar =
            c.pct >= 20 ? "#E5484D" : c.pct >= 10 ? "#F5A623" : "#22C05C";
          return (
            <div key={c.whatsappId}>
              <div
                style={{
                  display: "flex",
                  fontSize: 11.5,
                  fontWeight: 800,
                  color: V.textStrong,
                }}
              >
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: level }}>{c.pct}%</span>
              </div>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: V.track,
                  marginTop: 4,
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(100, c.pct)}%`,
                    background: bar,
                    borderRadius: 3,
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: V.labelMuted,
                  marginTop: 3,
                }}
              >
                {`${c.semResposta} de ${c.total} mensagens`}
              </div>
            </div>
          );
        })
      ) : (
        <span style={{ fontSize: 11, color: V.labelMuted }}>
          Sem mensagens hoje
        </span>
      )}
    </Card>
  </div>
);

export default Operations;
